import { CardActionArea,ImageList,ImageListItem,Skeleton,Stack,Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiError } from "react-icons/bi";
import AttToken from "../helpers/attToken";
import Files from "../service/Files";
import Reports from "../service/Reports";
import ModalImagePreview from "./Reports/Modal/ModalImagePreview";

const filesService = new Files();
const reportsService = new Reports();

const ImagesCmp = ({ images }: { images: string[] }) => {

  const [image, setImage] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<{ [key: string]: boolean }>({});
  const { t } = useTranslation('translation');

  const compressImage = async (blob: Blob, quality = 0.6): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;
        const maxWidth = 800;

        if (width > maxWidth) {
          const scale = maxWidth / width;
          width = maxWidth;
          height = height * scale;
        }

        canvas.width = width;
        canvas.height = height;

        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (compressedBlob) => {
            if (compressedBlob) {
              const compressedUrl = URL.createObjectURL(compressedBlob);
              resolve(compressedUrl);
            }
          },
          "image/jpeg",
          quality
        );
      };
      img.src = url;
    });
  };

  const fetchImagesInBatches = async (urls: string[], batchSize = 4) => {
    const results: string[] = [];

for (let i = 0; i < urls.length; i += batchSize) {
  const batch = urls.slice(i, i + batchSize);

  const batchResults = await Promise.all(
    batch.map(async (image) => {
      const response = await fetch(image);

      if (!response.ok) {
        throw new Error(`Erro ao baixar imagem: ${image}`);
      }

      const blob = await response.blob();

      const shouldCompress = blob.size > 500_000;

      if (shouldCompress) {
        return await compressImage(blob);
      }

      return URL.createObjectURL(blob);
    })
  );

  results.push(...batchResults);
}

    return results;
  };

  const useImages = (images: string[]) => {
    return useQuery({
      queryKey: ['images', images],

      queryFn: async () => {
        const token = await AttToken();

        let urls: string[] = [];
        if (token) {
          const response = await filesService.downloadFiles(token, 'web', '-', images);
          urls = response?.urls || [];
        } else {
          urls = await reportsService.getImages(images || []);
        }

        if (!urls.length) return [];

        return await fetchImagesInBatches(urls, 4);
      },

      enabled: images?.length > 0,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    });
  };

  const { data: returnedImg = [], isLoading } = useImages(images);

  const handleImageError = (imageSrc: string) => {
    setImageErrors(prev => ({ ...prev, [imageSrc]: true }));
  };

  return (
    <>
      {open &&
        <ModalImagePreview
          image={image}
          open={open}
          setOpen={setOpen}
        />}
      <ImageList gap={4} cols={3}
        rowHeight={window.location.pathname === '/notificacaoWeb' ? 120 : 150}>
        {isLoading
          ? images?.map((_ele, ind) => {
            return (
              <ImageListItem key={ind}>
                <Skeleton variant="rounded"
                  width={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}
                  height={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'} />;
              </ImageListItem>);
          })
          : <>

            {returnedImg?.map((item, i) => {
              const imageError = imageErrors[item];
              return (
                <CardActionArea onClick={() => {
                  if (!imageErrors[item]) {
                    setImage(item);
                    setOpen(true);
                  }
                }} key={`imagem ${i}`}>
                  <ImageListItem sx={{ width: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`, height: '150px' }}>
                    {imageError ? (
                      <div style={{
                        border: '1px gray solid',
                        borderRadius: 5,
                        width: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`,
                        height: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`,
                      }}>
                        <Stack direction='column' justifyContent='center' alignItems='center' spacing={1.5} color='gray' padding={1}>
                          <BiError size={25} />
                          <Typography color='textDisabled' fontWeight={700} component={'span'} textAlign='center'>
                            {t('users.imageNotFound')}
                          </Typography>
                        </Stack>
                      </div>
                    ) : (
                      <img
                        width={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}
                        height={window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}
                        onError={() => handleImageError(item)} // Call handleImageError with the image source
                        style={{
                          width: `${window.location.pathname === '/notificacaoWeb' ? '120px' : '150px'}`,
                          borderRadius: 2,
                          objectFit: 'cover',
                          maxWidth: '150px',
                          maxHeight: '150px'
                        }}
                        srcSet={`${item}`}
                        src={`${item}`}
                        alt={item}
                        loading="lazy"
                      />
                    )}
                  </ImageListItem>
                </CardActionArea>
              );
            })}
          </>
        }
      </ImageList>
    </>
  );
};

export default ImagesCmp;